import React, { createContext, useContext, useState, useEffect } from 'react';
import { useCookieConsent } from './CookieContext';

interface ThemeContextType {
  isDarkMode: boolean;
  toggleTheme: () => void;
  isPartyMode: boolean;
  togglePartyMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { consent } = useCookieConsent();
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Only load from localStorage if user has consented to preference cookies
    if (consent?.preferences) {
      const saved = localStorage.getItem('byteScan-theme');
      if (saved !== null) {
        return JSON.parse(saved);
      }
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [isPartyMode, setIsPartyMode] = useState(() => {
    // Only load from localStorage if user has consented to preference cookies
    if (consent?.preferences) {
      const saved = localStorage.getItem('byteScan-party');
      if (saved !== null) {
        return JSON.parse(saved);
      }
    }
    return false;
  });

  useEffect(() => {
    // Only save to localStorage if user has consented to preference cookies
    if (consent?.preferences) {
      localStorage.setItem('byteScan-theme', JSON.stringify(isDarkMode));
    }
  }, [isDarkMode, consent?.preferences]);

  useEffect(() => {
    // Only save to localStorage if user has consented to preference cookies
    if (consent?.preferences) {
      localStorage.setItem('byteScan-party', JSON.stringify(isPartyMode));
    }
  }, [isPartyMode, consent?.preferences]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const togglePartyMode = () => {
    setIsPartyMode((prev: boolean) => !prev);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, isPartyMode, togglePartyMode }}>
      {children}
    </ThemeContext.Provider>
  );
}; 